import { db } from "../config";
import { SearchRequest } from "./search.types";
import {
  collection,
  query,
  where,
  limit,
  getDocs,
  startAfter,
  orderBy,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";

import { blockService } from "@/services/usersBlocked/usersBlockedService";
import { getAge, getOppositeGender, slugify } from "@/utils";
import { filterDistance, filterUsernameSlug } from "@/utils/querys";
import { distanceBetween } from "geofire-common";

export class SearchService {
  private collectionRef = collection(db, "users");

  private mapDocToProfile(doc: any) {
    const data = doc.data();

    return {
      uid: doc.id,
      username: data.username,
      usernameSlug: data.usernameSlug,
      birthYear: data.birthYear,
      profileImage: data.profileImage || "",
      city: data.city || "",
      state: data.state || "",
      gender: data.gender,
      maritalStatus: data.maritalStatus,
      smoking: data.smoking,
      drinking: data.drinking,
      bodyType: data.bodyType,
      skinTone: data.skinTone,
      hairColor: data.hairColor,
      eyeColor: data.eyeColor,
      height: data.height,
      children: data.children,
      location: data.location,
      createdAt: data.createdAt,
      photosCount: Array.isArray(data.publicPhotos)
        ? data.publicPhotos.length
        : 0,
      isVerified: data.isVerified ?? false,
      planInfo: data.planInfo || null,
      closeAccount: data.closeAccount ?? false,
      visible: data.visible ?? true,
    };
  }

  private passesLocalFilters(profile: any, filters: any): boolean {
    // AGE
    if (
      typeof filters?.minAge === "number" ||
      typeof filters?.maxAge === "number"
    ) {
      if (!profile.birthYear) return false;

      const age = getAge(profile.birthYear);

      if (!age || Number.isNaN(age)) {
        return false;
      }

      if (typeof filters.minAge === "number" && age < filters.minAge) {
        return false;
      }

      if (typeof filters.maxAge === "number" && age > filters.maxAge) {
        return false;
      }
    }

    // HEIGHT
    if (
      typeof filters?.minHeight === "number" ||
      typeof filters?.maxHeight === "number"
    ) {
      const height = Number(profile.height);

      if (Number.isNaN(height)) {
        return false;
      }

      if (typeof filters.minHeight === "number" && height < filters.minHeight) {
        return false;
      }

      if (typeof filters.maxHeight === "number" && height > filters.maxHeight) {
        return false;
      }
    }

    // CITY
    if (filters?.city?.trim()) {
      const citySlug = slugify(filters.city);

      if (!profile.city) {
        return false;
      }

      if (!slugify(profile.city).includes(citySlug)) {
        return false;
      }
    }

    return true;
  }

  private toLatLng(geopoint: any) {
    if (!geopoint) return null;

    const lat = geopoint.latitude ?? geopoint.lat;
    const lng = geopoint.longitude ?? geopoint.lng;

    if (typeof lat !== "number" || typeof lng !== "number") {
      return null;
    }

    return { lat, lng };
  }

  async searchProfiles(
    params: SearchRequest & {
      lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
    },
  ): Promise<{
    profiles: any[];
    lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
  }> {
    try {
      const { uid, gender, filters, pageSize, lastDoc, userLocation } = params;

      const [blockedIdsArray] = await Promise.all([
        blockService.listBlockedIds(params.uid),
      ]);
      const blockedIds = new Set(blockedIdsArray);
      const genderToSearch = getOppositeGender(gender);

      // -----------------------------
      // BASE QUERY
      // -----------------------------
      const constraints: QueryConstraint[] = [
        where("closeAccount", "==", false),
        where("awaitingApproval", "==", false),
        where("visible", "==", true),
        where("gender", "in", genderToSearch),
      ];

      // -----------------------------
      // USERNAME FILTER + ORDER
      // -----------------------------
      if (filters?.keyword?.trim()) {
        const slug = slugify(filters.keyword);

        constraints.push(where("usernameSlug", ">=", slug));
        constraints.push(where("usernameSlug", "<=", slug + "\uf8ff"));
        constraints.push(orderBy("usernameSlug"));
      } else {
        const orderDirection = filters?.orderBy === "old" ? "asc" : "desc";

        constraints.push(orderBy("createdAt", orderDirection));
      }

      // -----------------------------
      // FIRESTORE FILTERS
      // -----------------------------
      if (filters?.maritalStatus) {
        constraints.push(where("maritalStatus", "==", filters.maritalStatus));
      }

      if (filters?.children) {
        constraints.push(where("children", "==", filters.children));
      }

      if (filters?.bodyType) {
        constraints.push(where("bodyType", "==", filters.bodyType));
      }

      if (filters?.skinTone) {
        constraints.push(where("skinTone", "==", filters.skinTone));
      }

      if (filters?.hairColor) {
        constraints.push(where("hairColor", "==", filters.hairColor));
      }

      if (filters?.eyeColor) {
        constraints.push(where("eyeColor", "==", filters.eyeColor));
      }

      if (filters?.smoking) {
        constraints.push(where("smoking", "==", filters.smoking));
      }

      if (filters?.drinking) {
        constraints.push(where("drinking", "==", filters.drinking));
      }

      // -----------------------------
      // DETECT LOCAL FILTERS
      // -----------------------------
      const hasLocalFilters =
        !!filters?.city ||
        typeof filters?.minAge === "number" ||
        typeof filters?.maxAge === "number" ||
        typeof filters?.minHeight === "number" ||
        typeof filters?.maxHeight === "number";

      const hasDistance =
        !!filters?.distance &&
        !!userLocation &&
        typeof filters.distance === "number";

      // -----------------------------
      // SMART LIMIT
      // -----------------------------
      const firestoreLimit =
        hasLocalFilters || hasDistance ? Math.max(pageSize * 5, 100) : pageSize;

      const results: any[] = [];

      let currentCursor = lastDoc ?? null;
      let finalCursor: QueryDocumentSnapshot<DocumentData> | null = null;

      const MAX_BATCHES = 20;
      let batchCount = 0;

      while (results.length < pageSize && batchCount < MAX_BATCHES) {
        batchCount++;

        const pageConstraints: QueryConstraint[] = [...constraints];

        if (currentCursor) {
          pageConstraints.push(startAfter(currentCursor));
        }

        pageConstraints.push(limit(firestoreLimit));

        const q = query(this.collectionRef, ...pageConstraints);

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          break;
        }

        currentCursor = snapshot.docs[snapshot.docs.length - 1];

        finalCursor = currentCursor;

        const batchProfiles = snapshot.docs
          .map((doc) => this.mapDocToProfile(doc))
          .filter((p) => !blockedIds.has(p.uid))
          .filter((p) => p.uid !== uid)
          .filter((p) => this.passesLocalFilters(p, filters));

        results.push(...batchProfiles);

        if (snapshot.docs.length < firestoreLimit) {
          break;
        }
      }

      const profiles = results.slice(0, pageSize);

      // -----------------------------
      // DISTANCE FILTER
      // -----------------------------
      // if (hasDistance) {
      //   const center = this.toLatLng(userLocation);

      //   if (center) {
      //     profiles = profiles.filter((profile) => {
      //       const profileGeo = this.toLatLng(profile.location?.geopoint);

      //       if (!profileGeo) {
      //         return true;
      //       }

      //       const distanceInKm =
      //         distanceBetween(
      //           [center.lat, center.lng],
      //           [profileGeo.lat, profileGeo.lng],
      //         ) * 6371;

      //       return distanceInKm <= filters.distance!;
      //     });

      //     /**
      //      * Ordena por distância
      //      */
      //     profiles.sort((a, b) => {
      //       const geoA = this.toLatLng(a.location?.geopoint);
      //       const geoB = this.toLatLng(b.location?.geopoint);

      //       if (!geoA && !geoB) return 0;
      //       if (!geoA) return 1;
      //       if (!geoB) return -1;

      //       const distanceA =
      //         distanceBetween([center.lat, center.lng], [geoA.lat, geoA.lng]) *
      //         6371;

      //       const distanceB =
      //         distanceBetween([center.lat, center.lng], [geoB.lat, geoB.lng]) *
      //         6371;

      //       return distanceA - distanceB;
      //     });
      //   }
      // }

      // -----------------------------
      // FINAL PAGE SIZE
      // -----------------------------

      return {
        profiles,
        lastDoc: finalCursor,
      };
    } catch (error) {
      console.error("Erro ao buscar perfis:", error);

      return {
        profiles: [],
        lastDoc: null,
      };
    }
  }
}

export const searchService = new SearchService();
